/* eslint-disable @typescript-eslint/naming-convention */
import { defaultCountryCode } from '@components/country-code/constants';
import {
  cities,
  countries
} from '@features/admin/components/location-details/cityMapping';
import { formatDate, parseDateString } from '@features/group-management/helper';
import { isValidPhoneNumber } from '@helpers/generic';
import { BloodGroups } from '@types';
import { checkArraysAreSame } from '@utils/generic';
import { getDefaultUserProfileTemplateHeaders } from '@utils/member';
import { snakeToCamel } from '@utils/stringFormat';
import { isInvalidEmail } from '@utils/validations';

import { defaultUserProfileTemplateHeaders } from './constants';
import {
  BranchMember,
  CustomField,
  ICustomFields,
  IFieldDisabledMap,
  IMemberFormFieldsState,
  MemberError,
  MemberFormFields,
  PossibleBooleanValues,
  UserProfileCustomField,
  UserProfileMemberInput
} from './types';

export function isValidBloodGroup(value: string): boolean {
  return Object.values(BloodGroups).includes(
    value.toUpperCase() as BloodGroups
  );
}

export const isValidInsureValue = (value: string): boolean =>
  Object.values(PossibleBooleanValues)
    .map((v) => v.toLowerCase())
    .includes((value?.toLowerCase() || '') as PossibleBooleanValues);

export const normalizeBoolCustomFieldValue = (
  value: string | null | undefined
): string => {
  if (!value) return '';
  const normalized = value?.toLowerCase();
  if (normalized === 'true' || normalized === 'yes') return 'true';
  if (normalized === 'false' || normalized === 'no') return 'false';
  return '';
};

export const getValidationErrorsOnUserProfile = (
  dataHeaders: string[],
  memberDetails: UserProfileMemberInput[],
  customFields: UserProfileCustomField[],
  countryCodeList: string[],
  countryCodeMapping: Record<string, string>
): string[] => {
  // Validate Headers
  const templateHeaders = getDefaultUserProfileTemplateHeaders(
    defaultUserProfileTemplateHeaders,
    customFields
  );

  if (
    checkArraysAreSame(
      dataHeaders.filter((header) => header !== '' && !header.startsWith('_')),
      templateHeaders
    ) === false
  ) {
    return [
      `Column names don't match. Please ensure they follow the latest template.`
    ];
  }

  const errors: string[] = [];
  const primaryNumbers: string[] = [];
  const altNumbers: string[] = [];
  const admissionNumbers: string[] = [];

  const countryList = countries.map((country) => country).concat('Others');

  const stateList = Object.keys(cities).map((state) => state);

  memberDetails.forEach((member: UserProfileMemberInput, index: number) => {
    const {
      name,
      primaryNumber,
      altNumber,
      primaryNumberCode,
      altNumberCode,
      email,
      dateOfBirth,
      admissionDate,
      admissionNumber,
      country,
      state,
      city,
      pincode,
      guardianName,
      bloodGroup,
      isInsured
    } = member;

    // Validate name
    if (!name || name.length < 3) errors.push(`Row ${index + 1}: Invalid name`);

    // Validate guardian name
    if (guardianName && guardianName.length < 3)
      errors.push(`Row ${index + 1}: Invalid Guardian Name`);

    // Validate primary number code
    if (
      !primaryNumberCode ||
      (primaryNumberCode && !countryCodeList.includes(`${primaryNumberCode}`))
    ) {
      errors.push(`Row ${index + 1}: Invalid Primary Number Country Code`);
    }

    const primaryNumberWithCode = `+${primaryNumberCode}${primaryNumber}`;

    //  validate primary number
    if (
      !primaryNumber ||
      (primaryNumber &&
        primaryNumberCode &&
        !isValidPhoneNumber(
          primaryNumber,
          countryCodeMapping[primaryNumberCode]
        ))
    )
      errors.push(`Row ${index + 1}: Invalid Primary number format`);

    // check if primary number and name are duplicate
    if (primaryNumbers.includes(`${name}_${primaryNumberWithCode}`))
      errors.push(`Row ${index + 1}: Duplicate Name and Primary Number`);

    // Validate alt number code
    if (altNumberCode && !countryCodeList.includes(`${altNumberCode}`)) {
      errors.push(`Row ${index + 1}: Invalid Alternative Number Country Code`);
    }

    const altNumberWithCode = `+${altNumberCode}${altNumber}`;
    // validate alternate number
    if (
      altNumber &&
      (!altNumberCode ||
        !isValidPhoneNumber(altNumber, countryCodeMapping[altNumberCode]))
    )
      errors.push(`Row ${index + 1}: Invalid Alternative number format`);

    // check primary and secondary numbers are same
    if (altNumber && primaryNumberWithCode === altNumberWithCode)
      errors.push(
        `Row ${index + 1}: Primary and Alternative Number should not be same`
      );

    // Validate email
    if (email && isInvalidEmail(email))
      errors.push(`Row ${index + 1}: Invalid Email`);

    if (bloodGroup && !isValidBloodGroup(bloodGroup))
      errors.push(`Row ${index + 1}: Invalid blood group format`);

    if (isInsured && !isValidInsureValue(isInsured))
      errors.push(`Row ${index + 1}: Invalid is insured value`);

    if (dateOfBirth) {
      const { date, error } = parseDateString(dateOfBirth);
      if (error) errors.push(`Row ${index + 1}: ${error}`);
      if (date && date.getTime() > new Date().setHours(0, 0, 0, 0))
        errors.push(
          `Row ${index + 1}: Date of birth should not be a future date`
        );
    }

    if (admissionDate) {
      const { error } = parseDateString(admissionDate);
      if (error) errors.push(`Row ${index + 1}: ${error}`);
    }

    if (admissionNumber && admissionNumbers.includes(admissionNumber)) {
      errors.push(`Row ${index + 1}: Duplicate Admission Number`);
    }

    if (country) {
      if (!countryList.includes(country))
        errors.push(
          `Row ${index + 1}: Country should be one of the provided list values`
        );
      if (country === 'Others' && (state || city || pincode))
        errors.push(
          `Row ${
            index + 1
          }: For Non-Indian countries, State, City, and Pincode fields should remain empty`
        );
    }

    if (state) {
      if (!stateList.includes(state))
        errors.push(
          `Row ${index + 1}: State should be one of the provided list values`
        );
      if (!country)
        errors.push(
          `Row ${index + 1}: Please select a Country before entering State`
        );
    }

    if (city && !state)
      errors.push(
        `Row ${index + 1}: Please select a State before entering City`
      );

    if (
      pincode &&
      !(/^\d+$/.test(pincode.toString()) && pincode.toString().length === 6)
    ) {
      errors.push(`Row ${index + 1}: Invalid Pincode`);
    }

    // Add Primary Number and AltNumber to array
    if (primaryNumber) primaryNumbers.push(`${name}_${primaryNumberWithCode}`);
    if (altNumber) altNumbers.push(`${name}_${altNumberWithCode}`);
    if (admissionNumber) admissionNumbers.push(admissionNumber);
  });

  return errors;
};

/**
 * Validates member details and returns errors per member in backend format.
 * Returns an array where each index corresponds to a member's errors.
 */
export const getValidationErrorsPerMember = (
  dataHeaders: string[],
  memberDetails: UserProfileMemberInput[],
  customFields: UserProfileCustomField[],
  countryCodeList: string[],
  countryCodeMapping: Record<string, string>
): { headerError: string | null; memberErrors: MemberError[][] } => {
  // Validate Headers
  const templateHeaders = getDefaultUserProfileTemplateHeaders(
    defaultUserProfileTemplateHeaders,
    customFields
  );

  if (
    checkArraysAreSame(
      dataHeaders.filter((header) => header !== '' && !header.startsWith('_')),
      templateHeaders
    ) === false
  ) {
    return {
      headerError: `Column names don't match. Please ensure they follow the latest template.`,
      memberErrors: []
    };
  }

  const memberErrors: MemberError[][] = [];
  const primaryNumbers: string[] = [];
  const altNumbers: string[] = [];
  const admissionNumbers: string[] = [];

  const countryList = countries.map((country) => country).concat('Others');
  const stateList = Object.keys(cities).map((state) => state);

  memberDetails.forEach((member: UserProfileMemberInput) => {
    const errors: MemberError[] = [];
    const {
      name,
      primaryNumber,
      altNumber,
      primaryNumberCode,
      altNumberCode,
      email,
      dateOfBirth,
      admissionDate,
      admissionNumber,
      country,
      state,
      city,
      pincode,
      guardianName,
      bloodGroup,
      isInsured
    } = member;

    // Validate name
    if (!name || name.length < 3) {
      errors.push({ field: 'name', message: 'Invalid name' });
    }

    // Validate guardian name
    if (guardianName && guardianName.length < 3) {
      errors.push({ field: 'guardianName', message: 'Invalid Guardian Name' });
    }

    // Validate primary number code
    if (
      !primaryNumberCode ||
      (primaryNumberCode && !countryCodeList.includes(`${primaryNumberCode}`))
    ) {
      errors.push({
        field: 'primaryNumberCode',
        message: 'Invalid Primary Number Country Code'
      });
    }

    const primaryNumberWithCode = `+${primaryNumberCode}${primaryNumber}`;

    // Validate primary number
    if (
      !primaryNumber ||
      (primaryNumber &&
        primaryNumberCode &&
        !isValidPhoneNumber(
          primaryNumber,
          countryCodeMapping[primaryNumberCode]
        ))
    ) {
      errors.push({
        field: 'primaryNumber',
        message: 'Invalid Primary number format'
      });
    }

    // Check if primary number and name are duplicate
    if (primaryNumbers.includes(`${name}_${primaryNumberWithCode}`)) {
      errors.push({
        field: 'primaryNumber',
        message: 'Duplicate Name and Primary Number'
      });
    }

    // Validate alt number code
    if (altNumberCode && !countryCodeList.includes(`${altNumberCode}`)) {
      errors.push({
        field: 'altNumberCode',
        message: 'Invalid Alternative Number Country Code'
      });
    }

    const altNumberWithCode = `+${altNumberCode}${altNumber}`;

    // Validate alternate number
    if (
      altNumber &&
      (!altNumberCode ||
        !isValidPhoneNumber(altNumber, countryCodeMapping[altNumberCode]))
    ) {
      errors.push({
        field: 'altNumber',
        message: 'Invalid Alternative number format'
      });
    }

    // Check primary and secondary numbers are same
    if (altNumber && primaryNumberWithCode === altNumberWithCode) {
      errors.push({
        field: 'altNumber',
        message: 'Primary and Alternative Number should not be same'
      });
    }

    // Validate email
    if (email && isInvalidEmail(email)) {
      errors.push({ field: 'email', message: 'Invalid Email' });
    }

    if (bloodGroup && !isValidBloodGroup(bloodGroup)) {
      errors.push({
        field: 'bloodGroup',
        message: 'Invalid blood group format'
      });
    }

    if (isInsured && !isValidInsureValue(isInsured)) {
      errors.push({ field: 'isInsured', message: 'Invalid is insured value' });
    }

    if (dateOfBirth) {
      const { date, error } = parseDateString(dateOfBirth);
      if (error) {
        errors.push({ field: 'dateOfBirth', message: error });
      }
      if (date && date.getTime() > new Date().setHours(0, 0, 0, 0)) {
        errors.push({
          field: 'dateOfBirth',
          message: 'Date of birth should not be a future date'
        });
      }
    }

    if (admissionDate) {
      const { error } = parseDateString(admissionDate);
      if (error) {
        errors.push({ field: 'admissionDate', message: error });
      }
    }

    if (admissionNumber && admissionNumbers.includes(admissionNumber)) {
      errors.push({
        field: 'admissionNumber',
        message: 'Duplicate Admission Number'
      });
    }

    if (country) {
      if (!countryList.includes(country)) {
        errors.push({
          field: 'country',
          message: 'Country should be one of the provided list values'
        });
      }
      if (country === 'Others' && (state || city || pincode)) {
        errors.push({
          field: 'country',
          message:
            'For Non-Indian countries, State, City, and Pincode fields should remain empty'
        });
      }
    }

    if (state) {
      if (!stateList.includes(state)) {
        errors.push({
          field: 'state',
          message: 'State should be one of the provided list values'
        });
      }
      if (!country) {
        errors.push({
          field: 'state',
          message: 'Please select a Country before entering State'
        });
      }
    }

    if (city && !state) {
      errors.push({
        field: 'city',
        message: 'Please select a State before entering City'
      });
    }

    if (
      pincode &&
      !(/^\d+$/.test(pincode.toString()) && pincode.toString().length === 6)
    ) {
      errors.push({ field: 'pincode', message: 'Invalid Pincode' });
    }

    // Validate custom field values by type
    const memberAsAny = member as Record<string, any>;
    customFields?.forEach((field) => {
      const camelKey = snakeToCamel(field.key);
      const rawValue = memberAsAny[field.label];
      if (!rawValue && rawValue !== 0) return;

      const strValue = `${rawValue}`.trim();
      if (!strValue) return;

      switch (field.type) {
        case 'bool':
          if (!isValidInsureValue(strValue)) {
            errors.push({
              field: camelKey,
              message: `${field.label} must be Yes or No`
            });
          }
          break;
        case 'number':
          if (Number.isNaN(Number(strValue))) {
            errors.push({
              field: camelKey,
              message: `${field.label} must be a valid number`
            });
          }
          break;
        case 'date': {
          const { error: dateErr } = parseDateString(strValue);
          if (dateErr) {
            errors.push({
              field: camelKey,
              message: `${field.label}: ${dateErr}`
            });
          }
          break;
        }
        case 'varchar':
          if (strValue.length > 100) {
            errors.push({
              field: camelKey,
              message: `${field.label} must be 100 characters or fewer`
            });
          }
          break;
        case 'textarea':
          if (strValue.length > 500) {
            errors.push({
              field: camelKey,
              message: `${field.label} must be 500 characters or fewer`
            });
          }
          break;
        default:
          break;
      }
    });

    // Add Primary Number and AltNumber to array for duplicate checking
    if (primaryNumber) primaryNumbers.push(`${name}_${primaryNumberWithCode}`);
    if (altNumber) altNumbers.push(`${name}_${altNumberWithCode}`);
    if (admissionNumber) admissionNumbers.push(admissionNumber);

    memberErrors.push(errors);
  });

  return { headerError: null, memberErrors };
};

export const getCreateMemberRequestBody = (
  memberList: UserProfileMemberInput[],
  orgCustomFields: UserProfileCustomField[] = []
): BranchMember[] => {
  const updatedList = memberList.map((member) => {
    const {
      id,
      name,
      primaryNumber,
      primaryNumberCode,
      altNumber,
      altNumberCode,
      email,
      dateOfBirth,
      guardianName,
      admissionNumber,
      admissionDate,
      addressLine1,
      addressLine2,
      country,
      state,
      city,
      pincode
    } = member;

    const memberAsAny = member as Record<string, any>;

    const customFields: CustomField[] = orgCustomFields.map((field) => {
      const rawValue = memberAsAny[field.label];
      if (field.type === 'bool' && rawValue) {
        return {
          key: field.key,
          value:
            `${rawValue}`.toLowerCase() ===
            PossibleBooleanValues.YES.toLowerCase()
              ? 'true'
              : 'false'
        };
      }
      return {
        key: field.key,
        value: rawValue ? `${rawValue}` : null
      };
    });

    return {
      id: id || '',
      name: name || null,
      mobile: primaryNumber?.toString().replace(/\s+/g, '') || null,
      country_code: primaryNumberCode || null,
      alternate_mobile: altNumber?.toString().replace(/\s+/g, '') || null,
      secondary_country_code: altNumberCode || null,
      email: email || null,
      dob: dateOfBirth ? formatDate(dateOfBirth) : null,
      guardian_name: guardianName || null,
      admission_number: admissionNumber?.toString() || null,
      admission_date: admissionDate ? formatDate(admissionDate) : null,
      address_line_1: addressLine1 || null,
      address_line_2: addressLine2 || null,
      city: city || null,
      state: state || null,
      country: country || null,
      pincode: pincode || null,
      custom_fields: customFields
    };
  });
  return updatedList;
};

export const handleFalsy = (input: any): string | null => {
  if (!input) return null;
  return input;
};

export const resetInvalidDate = (date: string | null): string => {
  if (!date || (date && new Date(date).getFullYear() === 1)) {
    return '';
  }
  return date;
};

export const getMemberFormFieldsStateFromBody = (
  memberBody: BranchMember,
  orgCustomFields: UserProfileCustomField[]
): IMemberFormFieldsState => {
  const {
    id,
    name,
    email,
    mobile,
    country_code,
    alternate_mobile,
    secondary_country_code,
    dob,
    guardian_name,
    admission_number,
    admission_date,
    address_line_1,
    address_line_2,
    city,
    state,
    country,
    pincode,
    custom_fields
  } = memberBody;

  const customFieldsState: Partial<IMemberFormFieldsState> = {};

  orgCustomFields?.forEach((field) => {
    const customField = custom_fields?.find((cf) => cf.key === field.key);
    customFieldsState[snakeToCamel(field.key) as ICustomFields] =
      customField?.value || '';
  });

  const validAdmissionDate = resetInvalidDate(admission_date);
  const validDob = resetInvalidDate(dob);

  return {
    id: id || '',
    name: name || '',
    email: email || '',
    primaryNumber: mobile || '',
    primaryCountryCode: country_code || defaultCountryCode,
    alternateNumber: alternate_mobile || '',
    alternateCountryCode: secondary_country_code || defaultCountryCode,
    dateOfBirth: validDob,
    guardianName: guardian_name || '',
    admissionNumber: admission_number || '',
    admissionDate: validAdmissionDate,
    addressLine1: address_line_1 || '',
    addressLine2: address_line_2 || '',
    city: city || '',
    state: state || '',
    country: country || '',
    pincode: pincode || '',
    bloodGroup: '',
    isInsured: '',
    counsellorName: '',
    ...customFieldsState
  };
};

export const getCreateMemberBodyWithCustomFields = (
  newMember: IMemberFormFieldsState,
  customFields: UserProfileCustomField[]
) => {
  const {
    id,
    name,
    email,
    primaryNumber,
    primaryCountryCode,
    alternateNumber,
    alternateCountryCode,
    dateOfBirth,
    guardianName,
    admissionNumber,
    admissionDate,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    pincode
  } = newMember;

  const customFieldsData =
    customFields?.reduce((acc: CustomField[], field) => {
      const value = newMember[snakeToCamel(field.key) as MemberFormFields];
      acc.push({
        key: field.key,
        value: handleFalsy(value)
      });
      return acc;
    }, []) || [];

  return {
    id: id || '',
    name: handleFalsy(name)?.trim() || '',
    email: handleFalsy(email),
    mobile: handleFalsy(primaryNumber),
    country_code: primaryCountryCode || defaultCountryCode,
    alternate_mobile: handleFalsy(alternateNumber),
    secondary_country_code: alternateNumber
      ? alternateCountryCode || defaultCountryCode
      : null,
    dob: handleFalsy(formatDate(dateOfBirth)),
    guardian_name: handleFalsy(guardianName),
    admission_number: admissionNumber ? admissionNumber.toString() : null,
    admission_date: handleFalsy(formatDate(admissionDate)),
    address_line_1: handleFalsy(addressLine1),
    address_line_2: handleFalsy(addressLine2),
    city: handleFalsy(city),
    state: handleFalsy(state),
    country: handleFalsy(country),
    pincode: handleFalsy(pincode),
    custom_fields: customFieldsData
  };
};

export const isMemberFormStateEqual = (
  state1: IMemberFormFieldsState,
  state2: IMemberFormFieldsState
): boolean => {
  const keys = [
    ...new Set([...Object.keys(state1), ...Object.keys(state2)])
  ] as MemberFormFields[];
  return keys.every((key) => (state1[key] ?? '') === (state2[key] ?? ''));
};

export const getFormattedCountryCode = (
  countryCode?: string | null
): string => {
  if (countryCode) {
    return `+${countryCode}`;
  }
  return '';
};

/** Edit locks: with admission # lock it; without, lock name + primary number. */
export const getEditMemberDisabledFieldsMap = (
  admissionNumber?: string | null
): IFieldDisabledMap =>
  admissionNumber
    ? { [MemberFormFields.ADMISSION_NUMBER]: true }
    : {
        [MemberFormFields.NAME]: true,
        [MemberFormFields.PRIMARY_NUMBER]: true
      };
